import React, { useCallback, useEffect, useRef } from 'react';
import { fetchAlbumMedia } from '../../store/slices/user/asyncThunks';
import { useDispatch, useSelector } from 'react-redux';
import { selectFetchAlbumMediaInfo, selectUser } from '../../store/slices/user/slice';
import {
  AiSellContainer,
  InfiniteScrollMarker,
  ListContainer,
  LoaderContainer,
  LoaderMoreContainer,
  MediaWrapper,
  NotValidMedia,
  PlayIconWrapper,
  SelectIconWrapper,
  StyledImage,
} from './AlbumMediaList.styles';
import Spinner from '../Spinner/Spinner';
import { ALBUM_MEDIA_TYPE, CRM_ORGANIZATION_MEMBER_ROLE, MEDIA_ASSET_SALE_TAG } from '../../utils/constants';
import { ReactComponent as PlayIcon } from '../../assets/icons/play-white.svg';
import { ReactComponent as CheckCircleEmptyIcon } from '../../assets/icons/check-circle-empty.svg';
import { ReactComponent as CheckCircleIcon } from '../../assets/icons/check-circle-broken.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { memo } from 'react';
import { Text14Light } from '../utils/Text/Text.styles';
import PriceBadge from '../PriceBadge/PriceBadge';

const AlbumMediaList = ({
  page,
  setPage,
  kind,
  tag,
  onMediaClick,
  exactMatch,
  onSelectClick = () => {},
  selectedItems = [],
  onEditClick = () => {},
  influencerId,
}) => {
  const dispatch = useDispatch();
  const loadMoreRef = useRef(null);
  const user = useSelector(selectUser);

  const { data, maxReached, pending, fetchMorePending } = useSelector(selectFetchAlbumMediaInfo);

  useEffect(() => {
    setPage(1);
    dispatch(fetchAlbumMedia({ page: 1, kind, tag, exactMatch, ...(influencerId && { influencerId }) }));
  }, [dispatch, kind, tag, setPage, exactMatch, influencerId]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !pending && !fetchMorePending && !maxReached) {
        setPage(prev => prev + 1);
        dispatch(
          fetchAlbumMedia({
            page: page + 1,
            kind,
            tag,
            exactMatch,
            ...(influencerId && { influencerId }),
          }),
        );
      }
    },
    [pending, maxReached, fetchMorePending, page, dispatch, kind, tag, setPage, exactMatch, influencerId],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, { root: null, threshold: 1.0 });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleIntersection]);

  return pending ? (
    <LoaderContainer>
      <Spinner width={120} height={120} />
    </LoaderContainer>
  ) : (
    <>
      <ListContainer>
        {data.map(media => {
          let notValid;
          if (tag.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID)) {
            notValid = !media.aiDescription || !media.fileId || !media.category;
          } else if (tag.includes(MEDIA_ASSET_SALE_TAG.BUNDLE)) {
            notValid = !media.fileId;
          } else {
            notValid = !media.fileId || !media.aiDescription;
          }

          return (
            <MediaWrapper
              key={media.id}
              onClick={() => {
                if (!notValid) {
                  onMediaClick(media);
                }
              }}>
              <StyledImage src={media.thumbnailUrl} />
              {notValid && (
                <NotValidMedia>
                  <Text14Light>Processing, please wait...</Text14Light>
                </NotValidMedia>
              )}
              {[
                CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
                CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
                CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
                CRM_ORGANIZATION_MEMBER_ROLE.CONTENT_MANAGER,
              ].includes(user.role) && (
                <>
                  {!notValid && (
                    <SelectIconWrapper
                      onClick={e => {
                        onSelectClick(media);
                        e.stopPropagation();
                      }}>
                      {selectedItems.find(m => m.id === media.id) ? (
                        <CheckCircleIcon color="#fff" width={24} height={24} />
                      ) : (
                        <CheckCircleEmptyIcon color="#fff" width={24} height={24} />
                      )}
                    </SelectIconWrapper>
                  )}
                  <SelectIconWrapper
                    $notValid={!notValid}
                    onClick={e => {
                      onEditClick(media);
                      e.stopPropagation();
                    }}>
                    <EditIcon color="#fff" />
                  </SelectIconWrapper>
                </>
              )}
              {media.kind === ALBUM_MEDIA_TYPE.VIDEO && (
                <PlayIconWrapper>
                  <PlayIcon fill="white" />
                </PlayIconWrapper>
              )}
              {!!media.sellWithAi && <AiSellContainer>AI</AiSellContainer>}
              <PriceBadge cost={media.cost} />
            </MediaWrapper>
          );
        })}
        {fetchMorePending && (
          <LoaderMoreContainer>
            <Spinner width={100} height={100} />
          </LoaderMoreContainer>
        )}
        {!!data.length && <InfiniteScrollMarker ref={loadMoreRef} />}
      </ListContainer>
    </>
  );
};

export default memo(AlbumMediaList);
